import { createContext, useContext, type ReactNode, useMemo } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { listDocuments, type DocumentSummary } from '../services/api/documents';
import { useAuth } from '../utils/authcontextt';

type DocumentsContextValue = UseQueryResult<DocumentSummary[], Error> & {
  documents: DocumentSummary[];
};

const DocumentsContext = createContext<DocumentsContextValue | undefined>(undefined);
const EMPTY_DOCUMENTS: DocumentSummary[] = [];

type DocumentsProviderProps = {
  children: ReactNode;
};

export const DocumentsProvider = ({ children }: DocumentsProviderProps) => {
  const { loading, user } = useAuth();

  const queryResult = useQuery({
    queryKey: ['documents', user?.id],
    queryFn: listDocuments,
    enabled: !loading && Boolean(user),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const documents = queryResult.data ?? EMPTY_DOCUMENTS;

  const value = useMemo<DocumentsContextValue>(() => {
    return {
      ...queryResult,
      documents,
    };
  }, [documents, queryResult]);

  return <DocumentsContext.Provider value={value}>{children}</DocumentsContext.Provider>;
};

export const useDocumentsContext = () => {
  const context = useContext(DocumentsContext);

  if (!context) {
    throw new Error('useDocumentsContext must be used within a DocumentsProvider');
  }

  return context;
};
